import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import Carousel, { useCarousel } from '_components/Carousel';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackPlpHeroCatNavClick } from '_utils/GA4/trackPlpHeroCatNavEvent';
import { PlpHeroNavCarousel } from './types';

import 'swiper/css';

import styles from './NavCarousel.module.scss';

export interface NavCarouselProps {
  navCarousel: PlpHeroNavCarousel;
  /** Rendered on top of the hero cover image */
  onImage: boolean;
  locale?: string;
  coOpBrand?: string | null;
}

/**
 * Normalizes a URL for the current-page comparison: strips the query string,
 * a trailing slash and a leading locale segment.
 */
const normalizeUrl = (url: string) =>
  url
    .split('?')[0]
    .replace(/^\/(en|fr)(?=\/)/, '')
    .replace(/\/$/, '');

// Rendered inside Carousel.Provider so it can read isScrollable — hides the
// arrows entirely rather than just disabling them when every tile already
// fits without scrolling.
const NavControls = ({
  prevLabel,
  nextLabel,
}: {
  prevLabel: string;
  nextLabel: string;
}) => {
  const {
    state: { isScrollable },
  } = useCarousel();

  if (!isScrollable) return null;

  return (
    <div className={styles.controls}>
      <Carousel.NavPrev
        aria-label={prevLabel}
        className={styles.navButton}
        iconSize={16}
        iconWeight="bold"
      />
      <Carousel.NavNext
        aria-label={nextLabel}
        className={styles.navButton}
        iconSize={16}
        iconWeight="bold"
      />
    </div>
  );
};

const NavCarousel = ({
  navCarousel,
  onImage,
  locale,
  coOpBrand,
}: NavCarouselProps) => {
  const { style, items } = navCarousel;
  const { asPath } = useRouter();
  const { t } = useTranslation('plp');

  const currentPath = normalizeUrl(asPath);
  const interactionLocation = asPath.split('?')[0];
  // The image style is only honoured once at least one tile actually resolved a
  // BigCommerce image. A document whose SKUs all fail to resolve would
  // otherwise keep the taller image layout with an empty slot in every tile;
  // falling back to the text layout degrades to a deliberate design instead.
  const showImages = style === 'image' && items.some((item) => item.image);

  return (
    <Carousel.Provider>
      <div
        className={cn(
          styles.container,
          onImage ? styles.onImage : styles.plain,
          !showImages && styles.labelOnly
        )}
      >
        <Carousel.Track
          className={styles.swiper}
          spaceBetween={8}
          breakpoints={{ 720: { spaceBetween: onImage ? 4 : 8 } }}
        >
          {items.map((item, index) => {
            const isActive = normalizeUrl(item.url) === currentPath;
            const href = `/${locale}${item.url}`;
            const image = showImages ? item.image : null;

            return (
              <Carousel.Slide
                key={`${item.label}-${item.url}`}
                className={styles.slide}
              >
                <a
                  href={href}
                  className={styles.tile}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={(e) => {
                    const data = {
                      interactionLocation,
                      linkUrl: href,
                      linkText: item.label,
                      coOpBrand: coOpBrand || null,
                      index,
                    };

                    // A modified click keeps this page alive, so the hit is not
                    // at risk and navigation must not be hijacked.
                    if (isModifiedClick(e)) {
                      trackPlpHeroCatNavClick({ data });
                      return;
                    }

                    e.preventDefault();
                    trackPlpHeroCatNavClick({
                      data,
                      eventCallback: () => {
                        window.location.href = href;
                      },
                    });
                  }}
                >
                  {/* The slot is held open even when this one tile failed to
                      resolve, so a partial outage keeps every label starting at
                      the same offset rather than reflowing the strip. */}
                  {showImages && (
                    <span className={styles.imageContainer}>
                      {image && (
                        <Image
                          src={image}
                          alt=""
                          fill
                          sizes="56px"
                          className={styles.image}
                        />
                      )}
                    </span>
                  )}
                  <span className={styles.label}>{item.label}</span>
                </a>
              </Carousel.Slide>
            );
          })}
        </Carousel.Track>
        <NavControls
          prevLabel={t('carousel.previous')}
          nextLabel={t('carousel.next')}
        />
      </div>
    </Carousel.Provider>
  );
};

export default NavCarousel;
